import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { message, Typography } from 'antd';
import { IoCheckmark, IoChevronDown } from 'react-icons/io5';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch } from 'react-redux';
import { BottomSheet } from 'react-spring-bottom-sheet';

import CourseAPI from '../../api/CourseAPI';
import HeaderTextAction from '../../components/Header/actions/HeaderTextAction';
import FilterButtonWithBottomSheet from '../../components/Header/components/FilterButtonWithBottomSheet';
import Header from '../../components/Header/Header';
import SearchBar from '../../components/Header/SearchBar';
import Loader from '../../components/Loader';
import MangoSelectionModal from '../../components/MangoSelectionModal/MangoSelectionModal';
import { setCourseTab } from '../../context/appReducer';
import { useTheme } from '../../context/ThemeProvider';
import { COURSES_PAGE_LIMIT } from '../../shared/constants';
import showAppError from '../../shared/error';
import { useAppNavigate, useAppSelector } from '../../shared/hooks';
import { dynamicItemTitle, hasFeatureAccess } from '../../shared/utils';
import { FEATURES } from '../../types/baseTypes';
import {
  CourseCategory,
  DurationBucketId,
  ICourseCategory,
  ICourseInstructor,
  ICoursesFilters,
  ICoursesState,
} from '../../types/courseTypes';
import { IMango } from '../../types/feedTypes';
import { ROUTES } from '../../types/routes';
import { ISubscription } from '../../types/userTypes';
import NoDataFound from '../Feed/components/NoDataFound/NoDataFound';
import CourseListCard from './CourseListCard/CourseListCard';
import NoCoursesContent from './NoCoursesContent';

const COURSE_FILTER_OPTIONS: Record<CourseCategory, string> = {
  all: 'All',
  'in-progress': 'In Progress',
  completed: 'Completed',
  expired: 'Expired',
  paid: 'Paid',
};

const ALL_OPTION_ID = '__all__';
const ALL_OPTION = { _id: ALL_OPTION_ID, title: 'All' };

const DURATION_BUCKETS: {
  _id: DurationBucketId;
  title: string;
  token: string;
}[] = [
  { _id: 'lte_30m', title: '≤ 30 minutes', token: '0:30' },
  { _id: '30m_1h', title: '30 mins - 1 hour', token: '30:60' },
  { _id: '1h_3h', title: '1 - 3 hours', token: '60:180' },
  { _id: '3h_6h', title: '3 - 6 hours', token: '180:360' },
  { _id: '6h_10h', title: '6 - 10 hours', token: '360:600' },
  { _id: '10h_15h', title: '10 - 15 hours', token: '600:900' },
  { _id: '15h_plus', title: '15+ hours', token: '900:' },
  { _id: 'not_set', title: 'Not Set', token: 'not_set' },
];

const initialFilters: ICoursesFilters = {
  search: '',
  category: 'all',
  mango: [],
  categoryIds: [],
  instructors: [],
  durationBuckets: [],
};

const initialState: ICoursesState = {
  loading: false,
  hasMore: true,
  courses: [],
  page: 1,
  limit: COURSES_PAGE_LIMIT,
};

interface Props {
  userId: string | null;
  subscriberMangoes?: ISubscription[];
}

const PurchasedCourses: React.FC<Props> = ({ userId }) => {
  const navigate = useAppNavigate();
  const dispatch = useDispatch();

  const { colors } = useTheme();

  const { hostMetadata, titleConfig } = useAppSelector((state) => state.app);
  const { subscribedMangoes } = useAppSelector((state) => state.user);

  const [filters, setFilters] = useState<ICoursesFilters>(initialFilters);
  const [categories, setCategories] = useState<ICourseCategory[]>([]);
  const [instructors, setInstructors] = useState<ICourseInstructor[]>([]);
  const [showMangoModal, setShowMangoModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showInstructorModal, setShowInstructorModal] = useState(false);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [pendingCategoryIds, setPendingCategoryIds] = useState<string[]>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [instructorSearchQuery, setInstructorSearchQuery] = useState('');
  const [state, setState] = useState<ICoursesState>(initialState);
  const [selectedFilter, setSelectedFilter] = useState<{
    [key: string]: string;
  }>({ _id: 'course', title: 'Course' });

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const onChangeFilters = <Key extends keyof ICoursesFilters>(
    key: Key,
    value: ICoursesFilters[Key],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const selectedDurationBuckets = useMemo(
    () =>
      DURATION_BUCKETS.filter((d) => filters.durationBuckets.includes(d._id)),
    [filters.durationBuckets],
  );

  const durationModalOptions = useMemo(
    () => [ALL_OPTION, ...DURATION_BUCKETS],
    [],
  );

  const durationModalSelected = useMemo(
    () =>
      filters.durationBuckets.length === 0
        ? [ALL_OPTION]
        : selectedDurationBuckets,
    [filters.durationBuckets.length, selectedDurationBuckets],
  );

  const instructorOptions = useMemo(
    () => instructors.map((i) => ({ _id: i._id, title: i.name })),
    [instructors],
  );

  const selectedInstructorOptions = useMemo(
    () => filters.instructors.map((i) => ({ _id: i._id, title: i.name })),
    [filters.instructors],
  );

  const instructorModalOptions = useMemo(() => {
    const q = instructorSearchQuery.trim().toLowerCase();
    if (!q) return [ALL_OPTION, ...instructorOptions];
    return instructorOptions.filter((i) => i.title.toLowerCase().includes(q));
  }, [instructorOptions, instructorSearchQuery]);

  const instructorModalSelected = useMemo(
    () =>
      filters.instructors.length === 0
        ? [ALL_OPTION]
        : selectedInstructorOptions,
    [filters.instructors.length, selectedInstructorOptions],
  );

  const mangoModalOptions = useMemo(
    () => [ALL_OPTION, ...subscribedMangoes],
    [subscribedMangoes],
  );

  const mangoModalSelected = useMemo(
    () => (filters.mango.length === 0 ? [ALL_OPTION] : filters.mango),
    [filters.mango],
  );

  const loadCourses = async (
    page: number = 1,
    currState: ICoursesState = state,
  ) => {
    if (currState.loading || !currState.hasMore) return;

    // if (state.courses?.length !== 0 && !filters.search) {
    //   return;
    // }

    const errorMsg = 'Failed to load courses.';

    const newState = { ...initialState, ...currState };

    if (page === 1) {
      newState.courses = [];
      newState.page = 1;
      newState.hasMore = true;
    }

    newState.loading = true;

    setState({ ...newState });

    try {
      const resp = await CourseAPI.getCourses({
        page,
        limit: newState.limit,
        search: filters.search,
        isPublished: true,
        onlySubscribed: true,
        category: filters.category,
        searchBy: selectedFilter._id,
        ...(filters.mango.length > 0 && {
          mango: filters.mango.map((m) => m._id),
        }),
        ...(filters.categoryIds.length > 0 && {
          categories: filters.categoryIds.join(','),
        }),
        ...(filters.instructors.length > 0 && {
          instructor: filters.instructors.map((i) => i._id),
        }),
        ...(selectedDurationBuckets.length > 0 && {
          durationRanges: selectedDurationBuckets.map((b) => b.token),
        }),
      });
      if (resp.status === 200) {
        const newCourses = [...resp.data.result];

        newState.courses = [...newState.courses, ...newCourses];

        newState.hasMore = newCourses.length >= newState.limit;

        if (newState.hasMore) {
          newState.page += 1;
        }
      } else {
        showAppError(resp.data, errorMsg);
      }
    } catch (error: any) {
      showAppError(error, errorMsg);
    } finally {
      newState.loading = false;
      setState({ ...newState });
    }
  };

  const getCategories = async () => {
    try {
      const resp = await CourseAPI.getCategories();
      if (resp.status === 200) {
        setCategories(resp.data.result);
      }
    } catch (error: any) {
      showAppError(error, 'Failed to load categories.');
    }
  };

  const getInstructors = async () => {
    try {
      const resp = await CourseAPI.getCourseInstructors();
      if (resp.status === 200) {
        setInstructors(resp.data.result);
      }
    } catch (error: any) {
      showAppError(error, 'Failed to load instructors.');
    }
  };

  useEffect(() => {
    loadCourses(1, initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, userId, selectedFilter]);

  useEffect(() => {
    dispatch(setCourseTab('purchased'));
    const element = document.getElementById('courses__content');

    if (element) {
      scrollContainerRef.current = element;
    }
    getCategories();
    getInstructors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCategoryModal) return;
    setPendingCategoryIds(filters.categoryIds);
    setCategorySearchQuery('');
    // Intentionally only when the sheet opens — avoid resetting while editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCategoryModal]);

  const filteredCategories = useMemo(() => {
    const q = categorySearchQuery.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, categorySearchQuery]);

  const togglePendingCategory = (id: string) => {
    setPendingCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const renderTagItem = useCallback(
    (
      value: string,
      key: string,
      onClick: () => void,
      isSelected: boolean,
      hasArrow: boolean = false,
    ) => (
      <div
        key={key}
        onClick={onClick}
        className={`tags-filter__tag ${isSelected ? 'active' : ''}`}>
        <Typography.Text>{value}</Typography.Text>
        {hasArrow && (
          <IoChevronDown
            size={18}
            color={isSelected ? colors.PRIMARY : colors.ICON}
          />
        )}
      </div>
    ),
    [colors.ICON, colors.PRIMARY],
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#fff',
          width: '100%',
        }}>
        <SearchBar
          onDebounce={(value) => {
            onChangeFilters('search', value);
          }}
          placeholder="Search"
          style={{
            width: '100%',
            borderBottomWidth: 1,
            borderColor: colors.BORDER,
          }}
        />
        <FilterButtonWithBottomSheet
          dateList={[
            { _id: 'course', title: 'Course' },
            { _id: 'section', title: 'Section' },
            { _id: 'chapter', title: 'Chapter' },
          ]}
          buttonTitle={selectedFilter?.title}
          searchKey="title"
          customHeaderTitle={`Search by`}
          customTitle={``}
          customDescription={''}
          noDataFoundTitle={``}
          filteredDataList={[selectedFilter]}
          setFilteredDataAction={(selectedData) => {
            setSelectedFilter(selectedData[0]);
          }}
          allowClear={false}
        />
      </div>

      <div className="tags-filter__container" style={{ borderTop: 'none' }}>
        <div className="tags-filter__wrapper">
          {Object.entries(COURSE_FILTER_OPTIONS).map(([key, value]) =>
            renderTagItem(
              value,
              key,
              () => onChangeFilters('category', key as CourseCategory),
              filters.category === key,
            ),
          )}

          {(() => {
            const count = filters.mango.length;
            const label =
              count === 0
                ? hostMetadata.offeringTitle.toTitleCase()
                : count === 1
                  ? filters.mango[0].title?.toTitleCase()
                  : `${filters.mango[0].title?.toTitleCase()} +${count - 1}`;
            return renderTagItem(
              label,
              'mango',
              () => setShowMangoModal(true),
              count > 0,
              true,
            );
          })()}
          {instructors.length > 0 &&
            renderTagItem(
              filters.instructors.length > 0
                ? `Instructors (${filters.instructors.length})`
                : 'Instructor',
              'instructor',
              () => setShowInstructorModal(true),
              filters.instructors.length > 0,
              true,
            )}
          {(() => {
            const count = selectedDurationBuckets.length;
            const label =
              count === 0
                ? 'Duration'
                : count === 1
                  ? selectedDurationBuckets[0].title
                  : `${selectedDurationBuckets[0].title} +${count - 1}`;
            return renderTagItem(
              label,
              'duration',
              () => setShowDurationModal(true),
              count > 0,
              true,
            );
          })()}
          {categories.length > 0 &&
            hasFeatureAccess(FEATURES.COURSE_RECOMMENDATION) &&
            renderTagItem(
              'Category',
              'category',
              () => setShowCategoryModal(true),
              Boolean(filters.categoryIds.length),
              true,
            )}
        </div>
      </div>

      <div className="courses__content">
        <InfiniteScroll
          hasMore={state.hasMore}
          loadMore={() => loadCourses(state.page)}
          useWindow={false}
          getScrollParent={() => scrollContainerRef.current as HTMLElement}
          loader={
            (state.loading || !userId || state.hasMore ? (
              <Loader
                style={{
                  margin: '20px 0',
                  width: '100%',
                }}
              />
            ) : undefined) as JSX.Element
          }>
          {state.courses.length > 0
            ? state.courses.map((item) => {
                return (
                  <CourseListCard
                    key={item._id}
                    onPress={() => {
                      if (
                        item.courseValidity &&
                        item.courseValidity.status === 'Expired'
                      )
                        message.error(
                          `${titleConfig.course.toTitleCase()} validity has expired!`,
                        );
                      else {
                        navigate(ROUTES.COURSE_OVERVIEW, {
                          courseId: item._id,
                        });
                      }
                    }}
                    type="purchased"
                    courseExpired={
                      item.courseValidity &&
                      item.courseValidity.status === 'Expired'
                    }
                    title={item.title}
                    coverImage={item.coverImage}
                    mangoes={item.mangoArr}
                    totalProgress={
                      typeof item.progress === 'number'
                        ? item.progress
                        : item.progress.progress || 0
                    }
                    totalLectures={item.numberOfChapters}
                    // paid course
                    purchaseRequired={item.paidCourse ?? false}
                    previewChapters={item.freeChapterCount || 0}
                    mangoToSell={item.mangoTosell}
                    newChapterTagList={item?.newChapterTag}
                    courseCategory={item.category}
                    rawCourseData={item}
                  />
                );
              })
            : null}
        </InfiniteScroll>

        <NoCoursesContent userId={userId} state={state} />
      </div>

      <MangoSelectionModal
        type="multiple"
        show={showMangoModal}
        closeModal={() => setShowMangoModal(false)}
        selectedMangoes={mangoModalSelected}
        mangoes={mangoModalOptions}
        allSentinelId={ALL_OPTION_ID}
        withoutFilterMangoList={subscribedMangoes}
        handleSave={(picked) => {
          const picks = (picked as IMango[]).filter(
            (m) => m._id !== ALL_OPTION_ID,
          );
          setFilters((p) => ({ ...p, mango: picks }));
          setShowMangoModal(false);
        }}
        noMangoComponent={
          <NoDataFound title={`No ${hostMetadata.offeringTitles} found`} />
        }
        customTitle={`Filter out ${dynamicItemTitle(
          'courses',
        )} based on ${dynamicItemTitle('mango')}`}
        customDescription={`Select ${dynamicItemTitle('mango')} from below`}
      />
      <MangoSelectionModal
        type="multiple"
        show={showInstructorModal}
        closeModal={() => {
          setShowInstructorModal(false);
          setInstructorSearchQuery('');
        }}
        selectedMangoes={instructorModalSelected}
        mangoes={instructorModalOptions}
        withoutFilterMangoList={[ALL_OPTION, ...instructorOptions]}
        allSentinelId={ALL_OPTION_ID}
        showSearchBar
        searchQuery={instructorSearchQuery}
        setSearchQuery={setInstructorSearchQuery}
        handleSave={(picked) => {
          const picks = (picked as { _id: string; title: string }[]).filter(
            (x) => x._id !== ALL_OPTION_ID,
          );
          setFilters((p) => ({
            ...p,
            instructors: picks.map((x) => ({ _id: x._id, name: x.title })),
          }));
          setShowInstructorModal(false);
          setInstructorSearchQuery('');
        }}
        noMangoComponent={<NoDataFound title="No instructors found" />}
        customHeaderTitle="Select instructors"
        customTitle="Filter by instructor"
        customDescription="Select one or more instructors"
      />
      <MangoSelectionModal
        type="multiple"
        show={showDurationModal}
        closeModal={() => setShowDurationModal(false)}
        selectedMangoes={durationModalSelected}
        mangoes={durationModalOptions}
        allSentinelId={ALL_OPTION_ID}
        handleSave={(picked) => {
          const picks = (picked as { _id: string }[]).filter(
            (x) => x._id !== ALL_OPTION_ID,
          );
          setFilters((p) => ({
            ...p,
            durationBuckets: picks.map((x) => x._id as DurationBucketId),
          }));
          setShowDurationModal(false);
        }}
        customHeaderTitle="Select duration"
        customTitle="Filter by course duration"
        customDescription="Pick one or more duration ranges"
      />
      <BottomSheet
        open={showCategoryModal}
        onDismiss={() => {
          setPendingCategoryIds(filters.categoryIds);
          setShowCategoryModal(false);
        }}
        className="category-selection-bottom-sheet"
        header={
          <Header
            title="Select category"
            canGoBack={false}
            noBorder
            actionItems={[
              <HeaderTextAction
                text="Done"
                onClick={() => {
                  onChangeFilters('categoryIds', pendingCategoryIds);
                  setShowCategoryModal(false);
                }}
              />,
            ]}
          />
        }>
        <div className="category-selection-bottom-sheet__body">
          <Typography.Title
            level={5}
            className="category-selection-bottom-sheet__title">
            Filter by category
          </Typography.Title>
          <Typography.Paragraph className="category-selection-bottom-sheet__description">
            Select one or more categories
          </Typography.Paragraph>
          <SearchBar
            onDebounce={setCategorySearchQuery}
            placeholder="Search categories..."
            style={{
              width: '100%',
              padding: 0,
              marginBottom: 12,
            }}
          />
          <div className="category-selection-bottom-sheet__list">
            {!categorySearchQuery && (() => {
              const isAllSelected = pendingCategoryIds.length === 0;
              return (
                <button
                  key="__all__"
                  type="button"
                  className={`category-selection-bottom-sheet__row ${
                    isAllSelected
                      ? 'category-selection-bottom-sheet__row--selected'
                      : ''
                  }`}
                  onClick={() => setPendingCategoryIds([])}>
                  <span className="category-selection-bottom-sheet__row-label">
                    ALL
                  </span>
                  <span
                    className={`category-selection-bottom-sheet__checkbox ${
                      isAllSelected
                        ? 'category-selection-bottom-sheet__checkbox--checked'
                        : ''
                    }`}
                    aria-hidden>
                    {isAllSelected ? (
                      <IoCheckmark size={14} color="#fff" />
                    ) : null}
                  </span>
                </button>
              );
            })()}
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => {
                const isSelected = pendingCategoryIds.includes(category._id);
                return (
                  <button
                    key={category._id}
                    type="button"
                    className={`category-selection-bottom-sheet__row ${
                      isSelected
                        ? 'category-selection-bottom-sheet__row--selected'
                        : ''
                    }`}
                    onClick={() => togglePendingCategory(category._id)}>
                    <span className="category-selection-bottom-sheet__row-label">
                      {category.name.toUpperCase()}
                    </span>
                    <span
                      className={`category-selection-bottom-sheet__checkbox ${
                        isSelected
                          ? 'category-selection-bottom-sheet__checkbox--checked'
                          : ''
                      }`}
                      aria-hidden>
                      {isSelected ? (
                        <IoCheckmark size={14} color="#fff" />
                      ) : null}
                    </span>
                  </button>
                );
              })
            ) : (
              <Typography.Text
                type="secondary"
                className="category-selection-bottom-sheet__empty">
                No categories match your search.
              </Typography.Text>
            )}
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default PurchasedCourses;
